import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import FieldRenderer from './FieldRenderer'
import { getFormFiles } from 'services/taskService'
import { isOnline as checkIsOnline } from 'ducks/offline/actions'

const toConditionList = value => {
  if (Array.isArray(value)) {
    return value.map(entry => String(entry).trim()).filter(entry => entry !== '')
  }
  if (value === null || value === undefined || value === '') return []
  return String(value).split(',').map(entry => entry.trim()).filter(entry => entry !== '')
}

const isNumericPair = (a, b) =>
  a !== '' && a !== null && a !== undefined &&
  b !== '' && b !== null && b !== undefined &&
  !Number.isNaN(Number(a)) && !Number.isNaN(Number(b))

const conditionListContains = (listValue, fieldValue) => {
  const list = toConditionList(listValue)
  if (list.length === 0) return false
  return toConditionList(fieldValue).some(entry => list.includes(entry))
}

const evaluateConditional = (conditional, responses) => {
  const fieldValue = responses[conditional.fieldKey]

  switch (conditional.operator) {
    case 'equals':
      return fieldValue === conditional.value
    case 'not_equals':
      return fieldValue !== conditional.value
    case 'greater_than':
      return isNumericPair(fieldValue, conditional.value) &&
        Number(fieldValue) > Number(conditional.value)
    case 'greater_or_equal':
      return isNumericPair(fieldValue, conditional.value) &&
        Number(fieldValue) >= Number(conditional.value)
    case 'less_than':
      return isNumericPair(fieldValue, conditional.value) &&
        Number(fieldValue) < Number(conditional.value)
    case 'less_or_equal':
      return isNumericPair(fieldValue, conditional.value) &&
        Number(fieldValue) <= Number(conditional.value)
    case 'in':
      return conditionListContains(conditional.value, fieldValue)
    case 'not_in':
      return !conditionListContains(conditional.value, fieldValue)
    case 'is_empty':
      return (!fieldValue && fieldValue !== 0 && fieldValue !== '0') ||
        (Array.isArray(fieldValue) && fieldValue.length === 0)
    case 'is_not_empty':
      return (!!fieldValue || fieldValue === 0 || fieldValue === '0') &&
        (!Array.isArray(fieldValue) || fieldValue.length > 0)
    default:
      return true
  }
}

const isSectionVisible = (section, responses) => {
  if (!section.conditionals || section.conditionals.length === 0) return true
  const method = section.conditionLogic === 'or' ? 'some' : 'every'
  return section.conditionals[method](cond => {
    const result = evaluateConditional(cond, responses)
    if (cond.action === 'hide') return !result
    if (cond.action === 'show') return result
    return true
  })
}

const isFieldVisible = (field, responses) => {
  if (!field.conditionals || field.conditionals.length === 0) return true
  const method = field.conditionLogic === 'or' ? 'some' : 'every'
  return field.conditionals[method](cond => {
    const result = evaluateConditional(cond, responses)
    if (cond.action === 'hide') return !result
    if (cond.action === 'show') return result
    return true
  })
}

const FormResponseViewer = ({
  schema,
  responses = {},
  taskId,
  task,
  onTaskRefresh
}) => {
  const [freshResponses, setFreshResponses] = useState(responses)

  // Refresh signed URLs for file fields on mount
  useEffect(() => {
    setFreshResponses(responses)
    if (!taskId || !schema?.sections || !checkIsOnline()) return

    const loadFreshUrls = async () => {
      try {
        const res = await getFormFiles(taskId)
        const files = res.data?.data || []
        if (files.length === 0) return

        const updated = { ...responses }
        const refreshFileValue = current => {
          const fid = current?.fileId || current?.file_id
          if (!current || typeof current !== 'object' || !fid) return current
          const match = files.find(f => f.id === fid)
          if (!match) return current
          return { ...current, url: match.url || current.url }
        }

        for (const section of schema.sections) {
          if (section.repeatable) {
            const entries = updated[section.key]
            if (Array.isArray(entries)) {
              updated[section.key] = entries.map(entry => {
                if (!entry || typeof entry !== 'object') return entry
                const refreshed = { ...entry }
                for (const field of section.fields || []) {
                  if (['file', 'image', 'video'].includes(field.type) && refreshed[field.key]) {
                    refreshed[field.key] = refreshFileValue(refreshed[field.key])
                  }
                }
                return refreshed
              })
            }
          } else {
            for (const field of section.fields || []) {
              if (['file', 'image', 'video'].includes(field.type)) {
                updated[field.key] = refreshFileValue(updated[field.key])
              }
            }
          }
        }
        setFreshResponses(updated)
      } catch {
        // Offline or error — use original responses
      }
    }

    loadFreshUrls()
  }, [taskId, responses, schema])

  if (!schema || !schema.sections) {
    return <EmptyMessage>No form data available.</EmptyMessage>
  }

  return (
    <ViewerContainer>
      {schema.sections.map(section => {
        if (!isSectionVisible(section, freshResponses)) return null
        return (
          <SectionCard key={section.key}>
            <SectionTitle>{section.title}</SectionTitle>
            {section.description && (
              <SectionDescription>{section.description}</SectionDescription>
            )}

            {section.repeatable ? (
              <div>
                {(Array.isArray(freshResponses[section.key])
                  ? freshResponses[section.key]
                  : []
                ).map((entry, entryIndex) => {
                  const entryResponses = { ...freshResponses, ...entry }
                  return (
                    <RepeatableEntry key={entryIndex}>
                      <EntryLabel>Entry {entryIndex + 1}</EntryLabel>
                      {(section.fields || []).map(field => {
                        if (!isFieldVisible(field, entryResponses)) return null
                        return (
                          <FieldRenderer
                            key={field.key}
                            field={field}
                            value={entry?.[field.key]}
                            commentValue={entry?._field_comments?.[field.key]}
                            attachmentsValue={entry?._field_attachments?.[field.key]}
                            readOnly
                            taskId={taskId}
                            task={task}
                            onTaskRefresh={onTaskRefresh}
                            entryKey={`${section.key}_${entryIndex}`}
                          />
                        )
                      })}
                    </RepeatableEntry>
                  )
                })}
                {(!freshResponses[section.key] ||
                  freshResponses[section.key].length === 0) && (
                  <NoEntriesMessage>No entries recorded.</NoEntriesMessage>
                )}
              </div>
            ) : (
              (section.fields || []).map(field => {
                if (!isFieldVisible(field, freshResponses)) return null
                return (
                  <FieldRenderer
                    key={field.key}
                    field={field}
                    value={freshResponses[field.key]}
                    commentValue={freshResponses._field_comments?.[field.key]}
                    attachmentsValue={freshResponses._field_attachments?.[field.key]}
                    readOnly
                    taskId={taskId}
                    task={task}
                    onTaskRefresh={onTaskRefresh}
                  />
                )
              })
            )}
          </SectionCard>
        )
      })}
    </ViewerContainer>
  )
}

export default FormResponseViewer

// --- Styled Components ---

const ViewerContainer = styled.div`
  max-width: 800px;
`

const EmptyMessage = styled.div`
  padding: 20px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  font-size: 14px;
`

const SectionCard = styled.div`
  margin-bottom: 24px;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 8px;
  padding: 20px;
  background-color: #fff;
`

const SectionTitle = styled.h3`
  margin: 0 0 4px 0;
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
`

const SectionDescription = styled.p`
  margin: 0 0 16px 0;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
`

const RepeatableEntry = styled.div`
  padding: 16px;
  margin-bottom: 12px;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 6px;
  background-color: ${({ theme }) => theme.colors.lightGrey};
`

const EntryLabel = styled.div`
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.haiti};
`

const NoEntriesMessage = styled.div`
  padding: 12px;
  color: ${({ theme }) => theme.colors.grey?.[700] || '#b3b3b3'};
  font-size: 13px;
`
